/**
 * TriColorGauge — 5-segment arc gauge with glow effect
 *
 * Distribution gauge: ext1(red) → warn1(yellow) → ok(green) → warn2(yellow) → ext2(red)
 * 270° arc starting from bottom-left (135°).
 *
 * Visual enhancements:
 * - SVG glow filter on colored segments (feGaussianBlur + feMerge)
 * - Precision tick marks around the arc (every 10°, major every 30°)
 * - Depth-enhanced background track
 * - Center accent dot
 *
 * GSAP animation is handled by parent via data-* attributes:
 * - data-gauge-seg: target strokeDasharray length
 * - data-gauge-offset: strokeDashoffset value
 * - data-gauge-ok: target OK percentage for count-up
 *
 * Designed for future reuse in native (React Native) via constant exports.
 */

import { useId } from "react";
import styles from "./TriColorGauge.module.css";

/* ── Gauge geometry (exportable for native reuse) ── */

export const GAUGE_R = 62;
export const GAUGE_CX = 80;
export const GAUGE_CY = 80;
export const GAUGE_ARC = 270; // degrees
export const GAUGE_CIRCUM = 2 * Math.PI * GAUGE_R;
export const GAUGE_DASH = (GAUGE_ARC / 360) * GAUGE_CIRCUM;
export const GAUGE_GAP = GAUGE_CIRCUM - GAUGE_DASH;
export const GAUGE_ROTATE = 135; // start at bottom-left

/* ── Color palette ── */

export const COLOR_EXTREME = "#ff3b30"; // red — airball, backboard
export const COLOR_WARNING = "#ffcc00"; // yellow — close miss
export const COLOR_OK = "#34c759"; // green — always green

/* ── Types ── */

export interface GaugeSegments {
  /** Extreme miss on left end (red, very small) */
  ext1: number;
  /** Close miss on left side (yellow) */
  warn1: number;
  /** OK / good range (green, always green) */
  ok: number;
  /** Close miss on right side (yellow) */
  warn2: number;
  /** Extreme miss on right end (red, very small) */
  ext2: number;
}

/* ── Helpers ── */

/** Convert percentage to arc stroke length */
export function gaugeSegLen(pct: number): number {
  return (pct / 100) * GAUGE_DASH;
}

/* ── Segment definition for SVG rendering ── */

interface SegDef {
  id: string;
  len: number;
  offset: number;
  color: string;
}

function buildSegments(s: GaugeSegments): SegDef[] {
  const s1 = gaugeSegLen(s.ext1);
  const s2 = gaugeSegLen(s.warn1);
  const s3 = gaugeSegLen(s.ok);
  const s4 = gaugeSegLen(s.warn2);
  const s5 = gaugeSegLen(s.ext2);
  return [
    { id: "ext1", len: s1, offset: 0, color: COLOR_EXTREME },
    { id: "warn1", len: s2, offset: s1, color: COLOR_WARNING },
    { id: "ok", len: s3, offset: s1 + s2, color: COLOR_OK },
    { id: "warn2", len: s4, offset: s1 + s2 + s3, color: COLOR_WARNING },
    { id: "ext2", len: s5, offset: s1 + s2 + s3 + s4, color: COLOR_EXTREME },
  ];
}

/* ── Tick marks geometry ── */

const TICK_COUNT = 27; // 27 intervals = 28 tick marks in 270°
const DEG_TO_RAD = Math.PI / 180;

function buildTicks() {
  const ticks: { id: string; x1: number; y1: number; x2: number; y2: number; isMajor: boolean }[] =
    [];
  for (let i = 0; i <= TICK_COUNT; i++) {
    const angleDeg = GAUGE_ROTATE + (i / TICK_COUNT) * GAUGE_ARC;
    const angleRad = angleDeg * DEG_TO_RAD;
    const isMajor = i % 3 === 0;
    const outerR = GAUGE_R + 14;
    const innerR = GAUGE_R + (isMajor ? 9 : 11);
    ticks.push({
      id: `t${angleDeg.toFixed(0)}`,
      x1: GAUGE_CX + Math.cos(angleRad) * innerR,
      y1: GAUGE_CY + Math.sin(angleRad) * innerR,
      x2: GAUGE_CX + Math.cos(angleRad) * outerR,
      y2: GAUGE_CY + Math.sin(angleRad) * outerR,
      isMajor,
    });
  }
  return ticks;
}

// Pre-compute (static, never changes)
const TICKS = buildTicks();

/* ── Component ── */

interface Props {
  segments: GaugeSegments;
  /** Label below the percentage (e.g. "Direction") */
  label: string;
  /** Left edge label (e.g. "L", "Short", "Flat") */
  leftLabel: string;
  /** Right edge label (e.g. "R", "Over", "High") */
  rightLabel: string;
}

export function TriColorGauge({ segments, label, leftLabel, rightLabel }: Props) {
  const uid = useId();
  const segs = buildSegments(segments);
  const glowId = `gauge-glow${uid}`;

  return (
    <svg viewBox="0 0 160 160" className={styles.svg} role="img" aria-label={`${label} gauge`}>
      <title>{label} gauge</title>

      {/* ── SVG Definitions ── */}
      <defs>
        {/* Glow filter: blur + merge with original for neon-like effect */}
        <filter id={glowId} x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {/* ── Tick marks (precision measurement aesthetic) ── */}
      {TICKS.map((tick) => (
        <line
          key={tick.id}
          x1={tick.x1}
          y1={tick.y1}
          x2={tick.x2}
          y2={tick.y2}
          stroke={tick.isMajor ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.08)"}
          strokeWidth={tick.isMajor ? 1.2 : 0.6}
          strokeLinecap="round"
        />
      ))}

      {/* ── Background arc (depth track) ── */}
      <circle
        cx={GAUGE_CX}
        cy={GAUGE_CY}
        r={GAUGE_R}
        fill="none"
        stroke="rgba(255,255,255,0.04)"
        strokeWidth="26"
        strokeLinecap="round"
        strokeDasharray={`${GAUGE_DASH} ${GAUGE_GAP}`}
        transform={`rotate(${GAUGE_ROTATE} ${GAUGE_CX} ${GAUGE_CY})`}
      />
      <circle
        cx={GAUGE_CX}
        cy={GAUGE_CY}
        r={GAUGE_R}
        fill="none"
        stroke="rgba(255,255,255,0.08)"
        strokeWidth="22"
        strokeLinecap="round"
        strokeDasharray={`${GAUGE_DASH} ${GAUGE_GAP}`}
        transform={`rotate(${GAUGE_ROTATE} ${GAUGE_CX} ${GAUGE_CY})`}
      />

      {/* ── Colored segments with glow filter ── */}
      <g filter={`url(#${glowId})`}>
        {segs.map((seg) => (
          <circle
            key={`seg-${label}-${seg.id}`}
            cx={GAUGE_CX}
            cy={GAUGE_CY}
            r={GAUGE_R}
            fill="none"
            stroke={seg.color}
            strokeWidth="22"
            strokeLinecap="butt"
            strokeDasharray={`${seg.len} ${GAUGE_CIRCUM - seg.len}`}
            strokeDashoffset={-seg.offset}
            transform={`rotate(${GAUGE_ROTATE} ${GAUGE_CX} ${GAUGE_CY})`}
            data-gauge-seg={seg.len.toFixed(2)}
            data-gauge-offset={seg.offset.toFixed(2)}
          />
        ))}
      </g>

      {/* ── Center accent dot ── */}
      <circle cx={GAUGE_CX} cy={GAUGE_CY} r={3} fill="rgba(255,255,255,0.25)" />

      {/* ── Center: OK percentage ── */}
      <text
        x={GAUGE_CX}
        y={GAUGE_CY - 10}
        textAnchor="middle"
        dominantBaseline="central"
        className={styles.pct}
        fill={COLOR_OK}
        data-gauge-ok={segments.ok}
      >
        {segments.ok}%
      </text>

      {/* ── Label below percentage ── */}
      <text x={GAUGE_CX} y={GAUGE_CY + 28} textAnchor="middle" className={styles.label}>
        {label}
      </text>

      {/* ── Edge labels (positioned below arc endpoints) ── */}
      <text x={24} y={150} textAnchor="start" className={styles.edge} fill={COLOR_WARNING}>
        {leftLabel}
      </text>
      <text x={136} y={150} textAnchor="end" className={styles.edge} fill={COLOR_WARNING}>
        {rightLabel}
      </text>
    </svg>
  );
}
